import {
  faCircleArrowRight,
  faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { CSSProperties, useState } from 'react';
import { FormattedMessage, defineMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Play } from '@sorare/core/src/atoms/icons/Play';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BodyL, BodyS, LabelM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { useMarketplaceEvents } from 'lib/events';

import { VideoDuration } from './VideoDuration';
import {
  competitionsVideo,
  getStartedVideo,
  introductionVideo,
  marketVideo,
  scoringVideo,
} from './data';

const StyledDialog = styled(Dialog)`
  &.onboarding {
    background: linear-gradient(
        180deg,
        #1e4ea9 23.47%,
        rgba(30, 78, 169, 0) 86.09%
      ),
      linear-gradient(319deg, #1510ff 16.49%, var(--c-rivals-friendlies) 82.68%);
  }
`;

const Root = styled(Vertical).attrs({ gap: 1 })`
  padding: 0 var(--double-unit) var(--double-unit);
  position: relative;
  isolation: isolate;

  &.withFooter {
    padding: 0 var(--double-unit);
  }
`;
const MainContent = styled(Vertical).attrs({ gap: 3 })`
  padding-bottom: var(--double-unit);
`;
const VideoWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 100%;
  height: 100%;
  position: relative;
  isolation: isolate;

  @media ${laptopAndAbove} {
    height: auto;
    aspect-ratio: 16 / 9;
  }
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  padding: var(--double-unit);
`;
const VideoPreview = styled(LinkBox)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  padding: var(--double-unit);
  overflow: hidden;

  &:not(.seen) {
    --border-gradient: var(--c-gradient-conversionCreditBorder);
    --border-size: 2px;
    ${borderGradientStyle}
  }
`;
const MarketVideoPreview = styled(VideoPreview)`
  border-radius: var(--double-unit);
  width: 100%;
  min-height: calc(44 * var(--unit));
`;
const MarketVideoPreviewBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(to top right, var(--c-nl-400), var(--c-nl-500)),
    center / cover no-repeat var(--url);
  mix-blend-mode: luminosity;

  &.seen {
    background:
      linear-gradient(to top right, var(--c-nl-900), var(--c-nl-900)),
      center / cover no-repeat var(--url);
  }
`;
const SecondaryVideoPreview = styled(VideoPreview)`
  border-radius: var(--intermediate-unit);
  width: 100%;
  min-height: calc(36 * var(--unit));
`;
const SecondaryVideoPreviewBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(to top right, var(--c-nl-600), var(--c-nl-600)),
    center / cover no-repeat var(--url);

  &.seen {
    background:
      linear-gradient(to top right, var(--c-nl-900), var(--c-nl-900)),
      center / cover no-repeat var(--url);
  }
`;
const LegibleBackground = styled.div`
  background: var(--c-nl-100);
  backdrop-filter: blur(2px);
`;
const GradientTitle = styled.span`
  font-family: var(--brand-font);
  text-transform: uppercase;
  font-weight: var(--t-bold);
  background: var(--c-gradient-conversionCreditBorder);
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;

  &.seen {
    background: none;
    background-clip: unset;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
    -moz-text-fill-color: unset;
    color: var(--c-nd-600);
  }
`;
const PartTitle = styled(GradientTitle)`
  font-size: var(--fs-12);
`;
const TransferMarketTitle = styled(GradientTitle)`
  font-size: var(--fs-32);
  line-height: 1;
`;
const PreviewFirstLine = styled.div`
  align-self: flex-start;
`;
const StyledLinkOverlay = styled(LinkOverlay)`
  &:hover,
  &:focus {
    opacity: 0.9;
  }
`;
const SecondaryVideoPreviewPlayButtonWrapper = styled.div`
  align-self: center;
`;
const Footer = styled(Horizontal)`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding-bottom: var(--double-unit);
  justify-content: flex-end;
  z-index: 2;

  &.expanded > * {
    flex: 1;
  }
`;
const BottomScrim = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: -1;
  background: linear-gradient(
    to top,
    #1e4ea9 23.47%,
    rgba(30, 78, 169, 0) 86.09%
  );
  height: calc(48 * var(--unit));

  &.foreground {
    z-index: 1;
  }
`;

const secondaryVideos = [
  introductionVideo,
  getStartedVideo,
  scoringVideo,
  competitionsVideo,
];

const partMessage = defineMessage({
  id: 'MarketVideoDialog.videoPreview.chapter',
  defaultMessage: 'Part {integer}',
});

type Props = {
  open: boolean;
  onClose: () => void;
  showOnboardingElements: boolean;
};
export const MarketVideoDialog = ({
  open,
  onClose,
  showOnboardingElements,
}: Props) => {
  const { locale } = useIntlContext();
  const [selectedVideo, setSelectedVideo] = useState<
    typeof marketVideo | (typeof secondaryVideos)[number] | null
  >(null);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const track = useMarketplaceEvents();

  const marketSources =
    marketVideo[
      locale as keyof Omit<
        typeof marketVideo,
        'key' | 'lifecycle' | 'part' | 'event'
      >
    ] || marketVideo.default;

  const onCloseVideo = () => {
    if (selectedVideo) {
      updateLifecycle(selectedVideo.lifecycle, true);
      if (selectedVideo.lifecycle === LIFECYCLE.sawMarketVideo) {
        track('Close Marketplace Tutorial Video');
      }
    }
    setSelectedVideo(null);
  };

  const marketVideoSeen = lifecycle?.[marketVideo.lifecycle];
  const sortedSecondaryVideos = secondaryVideos.sort((v1, v2) => {
    const seenV1 = !!lifecycle?.[v1.lifecycle];
    if (seenV1) {
      return 1;
    }
    const seenV2 = !!lifecycle?.[v2.lifecycle];
    return seenV2 ? -1 : 1;
  });

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      className={classNames({ onboarding: showOnboardingElements })}
      title={
        <Horizontal center>
          <LabelM bold as="h2">
            <FormattedMessage
              id="MarketVideoDialog.title"
              defaultMessage="Tutorial Videos"
            />
          </LabelM>
        </Horizontal>
      }
    >
      <Root className={classNames({ withFooter: showOnboardingElements })}>
        <MainContent>
          <MarketVideoPreview className={classNames({ seen: marketVideoSeen })}>
            <MarketVideoPreviewBackground
              style={
                {
                  '--url': `url(${marketSources.thumbnail})`,
                } as CSSProperties
              }
              className={classNames({ seen: marketVideoSeen })}
            />
            <PreviewFirstLine>
              {marketVideoSeen ? (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  color="var(--c-onBase-success)"
                />
              ) : (
                <VideoDuration durationInMinutes={marketSources.duration} />
              )}
            </PreviewFirstLine>
            <Vertical gap={2} center className="text-center">
              <LegibleBackground>
                <PartTitle className={classNames({ seen: marketVideoSeen })}>
                  <FormattedMessage
                    {...partMessage}
                    values={{ integer: marketVideo.part }}
                  />
                </PartTitle>
              </LegibleBackground>
              <LegibleBackground>
                <TransferMarketTitle
                  className={classNames({ seen: marketVideoSeen })}
                >
                  <FormattedMessage
                    id="MarketVideo.transferMarket"
                    defaultMessage="Transfer Market"
                  />
                </TransferMarketTitle>
              </LegibleBackground>
            </Vertical>
            <SBHorizontal gap={4}>
              <LegibleBackground>
                <BodyS>
                  <FormattedMessage
                    id="MarketVideo.startYourJourney2"
                    defaultMessage="Start your journey with this beginner guide!"
                  />
                </BodyS>
              </LegibleBackground>
              <StyledLinkOverlay
                as={ButtonBase}
                onClick={() => {
                  track(marketVideo.event);
                  setSelectedVideo(marketVideo);
                }}
              >
                <IconButton
                  size="medium"
                  aria-label="play-video-button"
                  asSpan
                  color={marketVideoSeen ? 'quaternary' : 'secondary'}
                >
                  <Play />
                </IconButton>
              </StyledLinkOverlay>
            </SBHorizontal>
          </MarketVideoPreview>
          <Vertical gap={2}>
            <Vertical gap={0}>
              <BodyL bold>
                <FormattedMessage {...glossary.continue} />
              </BodyL>
              <BodyL color="var(--c-nd-700)">
                <FormattedMessage
                  id="MarketVideoDialog.subtitle"
                  defaultMessage="Master the basics"
                />
              </BodyL>
            </Vertical>
            <Scrollable itemToDisplay={2} overhang={4}>
              {sortedSecondaryVideos.map(videoData => {
                const sources =
                  videoData[
                    locale as keyof Omit<
                      typeof videoData,
                      'key' | 'title' | 'lifecycle' | 'part' | 'event'
                    >
                  ] || videoData.default;
                const seen = lifecycle?.[videoData.lifecycle];
                return (
                  <SecondaryVideoPreview
                    key={videoData.key}
                    className={classNames({ seen })}
                  >
                    <SecondaryVideoPreviewBackground
                      style={
                        {
                          '--url': `url(${sources.thumbnail})`,
                        } as CSSProperties
                      }
                      className={classNames({ seen })}
                    />
                    <PreviewFirstLine>
                      {seen ? (
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          color="var(--c-onBase-success)"
                        />
                      ) : (
                        <VideoDuration durationInMinutes={sources.duration} />
                      )}
                    </PreviewFirstLine>
                    <SecondaryVideoPreviewPlayButtonWrapper>
                      <StyledLinkOverlay
                        as={ButtonBase}
                        onClick={() => {
                          track(videoData.event);
                          setSelectedVideo(videoData);
                        }}
                      >
                        <IconButton
                          size="medium"
                          asSpan
                          color={seen ? 'quaternary' : 'secondary'}
                        >
                          <Play />
                        </IconButton>
                      </StyledLinkOverlay>
                    </SecondaryVideoPreviewPlayButtonWrapper>
                    <LegibleBackground>
                      <Vertical gap={0}>
                        <PartTitle className={classNames({ seen })}>
                          <FormattedMessage
                            {...partMessage}
                            values={{ integer: videoData.part }}
                          />
                        </PartTitle>
                        <BodyS
                          color={seen ? 'var(--c-nd-600)' : 'var(--c-white)'}
                        >
                          <FormattedMessage {...videoData.title} />
                        </BodyS>
                      </Vertical>
                    </LegibleBackground>
                  </SecondaryVideoPreview>
                );
              })}
            </Scrollable>
          </Vertical>
        </MainContent>
        {!!showOnboardingElements && (
          <>
            <BottomScrim
              className={classNames({ foreground: !marketVideoSeen })}
            />
            <Footer className={classNames({ expanded: marketVideoSeen })}>
              <Button
                size="medium"
                color={marketVideoSeen ? 'secondary' : 'quaternary'}
                onClick={onClose}
              >
                <Horizontal>
                  <FormattedMessage {...glossary.finish} />
                  <FontAwesomeIcon icon={faCircleArrowRight} />
                </Horizontal>
              </Button>
            </Footer>
          </>
        )}
      </Root>
      {!!selectedVideo && (
        <Dialog
          onClose={onCloseVideo}
          open={!!selectedVideo}
          maxWidth="md"
          fullWidth
          hideHeader
        >
          {({ CloseButton }) => (
            <VideoWrapper>
              <CloseButtonWrapper>
                <CloseButton onClose={onCloseVideo} />
              </CloseButtonWrapper>
              <iframe
                width="100%"
                height="100%"
                src={
                  selectedVideo[
                    locale as keyof Omit<
                      typeof selectedVideo,
                      'key' | 'title' | 'lifecycle' | 'part' | 'event'
                    >
                  ]?.src || selectedVideo.default.src
                }
                title="YouTube video player"
                style={{ border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </VideoWrapper>
          )}
        </Dialog>
      )}
    </StyledDialog>
  );
};
